import React, { useState } from 'react';
import { 
  LocalizedText, 
  LocalizedDate, 
  LocalizedWeight, 
  LocalizedDuration, 
  LocalizedVolume,
  WorkoutCount,
  ExerciseCount,
  SetCount,
  CyrillicInput
} from './';
import { useLocalization } from '../hooks/useLocalization';

/**
 * LocalizationDemo - Demo component for testing localization features
 */
export const LocalizationDemo: React.FC = () => {
  const { t, formatRelativeTime } = useLocalization();
  const [testText, setTestText] = useState('');
  const [testComment, setTestComment] = useState('');

  const sampleDate = new Date();
  const pastDate = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000); // 2 days ago

  return (
    <div className="localization-demo container mx-auto px-4 py-6 pb-20">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">🌍 Демо локализации</h1>
        <p className="text-gray-600">Тестирование русской локализации и форматирования</p>
      </div>

      <div className="space-y-6">
        {/* Text Localization */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">📝 Локализация текста</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Ключи перевода:</strong>
              <ul className="mt-2 space-y-1">
                <li><LocalizedText textKey="loading" /></li>
                <li><LocalizedText textKey="error" /></li>
                <li><LocalizedText textKey="success" /></li>
                <li><LocalizedText textKey="workoutCompleted" /></li>
                <li><LocalizedText textKey="networkError" /></li>
              </ul>
            </div>
            <div>
              <strong>Навигация:</strong>
              <ul className="mt-2 space-y-1">
                <li><LocalizedText textKey="home" /></li>
                <li><LocalizedText textKey="exercises" /></li>
                <li><LocalizedText textKey="programs" /></li>
                <li><LocalizedText textKey="workout" /></li>
                <li><LocalizedText textKey="analytics" /></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Date Formatting */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">📅 Форматирование дат</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>Короткий формат:</span>
              <LocalizedDate date={sampleDate} format="short" />
            </div>
            <div className="flex justify-between">
              <span>Средний формат:</span>
              <LocalizedDate date={sampleDate} format="medium" />
            </div>
            <div className="flex justify-between">
              <span>Длинный формат:</span>
              <LocalizedDate date={sampleDate} format="long" />
            </div>
            <div className="flex justify-between">
              <span>С временем:</span>
              <LocalizedDate date={sampleDate} format="medium" showTime />
            </div>
            <div className="flex justify-between">
              <span>Относительное время:</span>
              <LocalizedDate date={pastDate} relative />
            </div>
          </div>
        </div>

        {/* Number Formatting */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">🔢 Форматирование чисел</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>Вес:</span>
              <LocalizedWeight weight={82.5} />
            </div>
            <div className="flex justify-between">
              <span>Длительность:</span>
              <LocalizedDuration minutes={75} />
            </div>
            <div className="flex justify-between">
              <span>Объем (малый):</span>
              <LocalizedVolume volume={850} />
            </div>
            <div className="flex justify-between">
              <span>Объем (большой):</span>
              <LocalizedVolume volume={2500} />
            </div>
          </div>
        </div>

        {/* Pluralization */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">📊 Множественные числа</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span>1 тренировка:</span>
              <WorkoutCount count={1} />
            </div>
            <div className="flex justify-between">
              <span>2 тренировки:</span>
              <WorkoutCount count={2} />
            </div>
            <div className="flex justify-between">
              <span>5 тренировок:</span>
              <WorkoutCount count={5} />
            </div>
            <div className="flex justify-between">
              <span>21 упражнение:</span>
              <ExerciseCount count={21} />
            </div>
            <div className="flex justify-between">
              <span>3 подхода:</span>
              <SetCount count={3} />
            </div>
          </div>
        </div>

        {/* Cyrillic Input Validation */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">⌨️ Валидация кириллицы</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Название программы (обязательное поле):
              </label>
              <CyrillicInput
                value={testText}
                onChange={setTestText}
                placeholder="Введите название программы..."
                required
                maxLength={50}
                showValidation
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Комментарий к тренировке:
              </label>
              <CyrillicInput
                value={testComment}
                onChange={setTestComment}
                placeholder="Добавьте комментарий..."
                type="textarea"
                maxLength={200}
                showValidation
              />
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Правила валидации:</h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Разрешены русские буквы (а-я, А-Я)</li>
              <li>• Разрешены цифры (0-9)</li>
              <li>• Разрешены пробелы и знаки препинания</li>
              <li>• Автоматическая нормализация (ё → е)</li>
              <li>• Удаление лишних пробелов</li>
            </ul>
          </div>
        </div>

        {/* Test Results */}
        {(testText || testComment) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">✅ Результаты валидации</h2>
            <div className="space-y-3">
              {testText && (
                <div>
                  <strong>Название программы:</strong>
                  <div className="mt-1 p-2 bg-gray-50 rounded text-sm font-mono">
                    "{testText}"
                  </div>
                </div>
              )}
              {testComment && (
                <div>
                  <strong>Комментарий:</strong>
                  <div className="mt-1 p-2 bg-gray-50 rounded text-sm font-mono">
                    "{testComment}"
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Usage Examples */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">💡 Примеры использования</h2>
          <div className="text-sm space-y-2">
            <p><strong>Правильный ввод:</strong> "Силовая программа А", "Отличная тренировка!", "Жим лежа 3×8"</p>
            <p><strong>Неправильный ввод:</strong> "Power Program", "Great workout!", "Bench press"</p>
            <p><strong>Автокоррекция:</strong> "Программа №1" → "Программа №1", "Тёст" → "Тест"</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalizationDemo;